import { useMemo } from "react";
import { Card } from "@/types/v2/Card";
import { Employee } from "@/types/v2/Employee";
import {
  DispatchStats,
  TechnicianStats,
  DispatchViewMode,
  JobState,
} from "@/types/v2/DispatchView";
import {
  calculateEmployeeHoursByStateFromCard,
  getCardsForDispatchView,
} from "@/lib/dispatchCalculations";
import { capitalize } from "@/lib/utils";
import { getServiceAdvisorColor } from "@/lib/employees";

const ZERO_HOURS_BY_STATE: Record<JobState, number> = {
  pending: 0,
  approved: 0,
  completed: 0,
  declined: 0,
};

const roundHours = (value: number) => Math.round(value * 100) / 100;

const roundHoursByState = (
  record: Record<JobState, number>
): Record<JobState, number> => ({
  pending: roundHours(record.pending),
  approved: roundHours(record.approved),
  completed: roundHours(record.completed),
  declined: roundHours(record.declined),
});

/**
 * Hook to calculate dispatch statistics from cards and employees
 * @param boardCards - Cards currently on the board
 * @param completedCards - Additional completed cards (for week mode)
 * @param employees - All employees (roster is the caller's responsibility —
 *   e.g. useTechnicians() applies the shop's technicians-only setting)
 * @param mode - View mode (board or week)
 * @returns Dispatch statistics including totals and per-employee breakdown
 */
export function useDispatchStats(
  boardCards: Card[],
  completedCards: Card[],
  employees: Employee[],
  mode: DispatchViewMode
): DispatchStats {
  return useMemo(() => {
    const visibleTechnicians = employees.filter((emp) => !emp.hideInShopHero);
    const cards = getCardsForDispatchView(boardCards, mode, completedCards);

    const technicianStatsMap = new Map<string, TechnicianStats>();
    visibleTechnicians.forEach((tech) => {
      technicianStatsMap.set(tech.id, {
        technicianId: tech.id,
        technicianName: `${capitalize(tech.firstName)} ${capitalize(tech.lastName)}`,
        technicianColor: getServiceAdvisorColor(tech.id, visibleTechnicians),
        hoursByState: { ...ZERO_HOURS_BY_STATE },
      });
    });

    const totalHoursByState: Record<JobState, number> = {
      ...ZERO_HOURS_BY_STATE,
    };

    cards.forEach((card) => {
      const employeeHours = calculateEmployeeHoursByStateFromCard(card);
      employeeHours.forEach((hoursByState, employeeId) => {
        const stats = technicianStatsMap.get(employeeId);
        if (!stats) return; // not in the visible roster, skip

        (Object.keys(hoursByState) as JobState[]).forEach((state) => {
          stats.hoursByState[state] += hoursByState[state];
          totalHoursByState[state] += hoursByState[state];
        });
      });
    });

    const technicianBreakdown = Array.from(technicianStatsMap.values()).map(
      (stats) => ({
        ...stats,
        hoursByState: roundHoursByState(stats.hoursByState),
      })
    );

    return {
      totalHoursByState: roundHoursByState(totalHoursByState),
      averageApprovedHoursPerTechnician:
        visibleTechnicians.length > 0
          ? roundHours(totalHoursByState.approved / visibleTechnicians.length)
          : 0,
      technicianBreakdown,
    };
  }, [boardCards, completedCards, employees, mode]);
}
